import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { InputAdornment, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

export default function EmpExpNewEditForm({ desgData, cntryData, filterCurrency, trigger ,switchState, onEmptyExperience}) {
  const { control, setValue, watch, resetField } = useFormContext();

   const defaultCountry = {
      countriesId: 101,
      countryName: 'India',
      shortName: 'IND',
      numericCode: '356',
      phoneCode: '91',
      countryCapital: 'New Delhi',
      currency: 'INR',
      currencyName: 'Indian rupee',
      currencySymbol: '₹',
      nativeName: 'भारत',
      tld: '.in',
      region: 'Asia',
      timezones:
        "[{zoneName:'Asia\\/Kolkata',gmtOffset:19800,gmtOffsetName:'UTC+05:30',abbreviation:'IST',tzName:'Indian Standard Time'}]",
      startDate: null,
      endDate: null,
    };

  // Set default currency
  const cur = defaultCountry;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'empExp',
  });

  const handleAdd = () => {
    append({
      expSrno: 0,
      compNm: '',
      designation: null,
      ctc: 0,
      ctccurncy: cur||null,
      country: null,
      expfromDate: null,
      exptoDate: null,
    });
  };

  const handleRemove = (index) => {
    remove(index);
  };

    // Only call onEmptyExperience() after mount and update, not on initial render to prevent inconsistency
    const hasMounted = useRef(false);
  
    useEffect(() => {
      if (hasMounted.current && fields.length === 0) {
        onEmptyExperience?.();
      }
      hasMounted.current = true;
    }, [fields.length, onEmptyExperience]);
  
  return (
    <>
      <Box
        mt={4}
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        {/* <Typography variant="subtitle2" sx={{ mb: 3 }}>
          Experience Details:
        </Typography> */}
        <Box>
          <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
            Experience Details:
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Experience
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={3}>
        {fields.map((item, index) => (
          <Stack key={item.id} spacing={1.5}>
            <Typography variant="subtitle2" sx={{ color: 'primary', mb: 2 }}>
              Experience {index + 1}:
            </Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'none' } }}>
                <RHFTextField name={`empExp[${index}].expSrno`} />
              </Box>
              <RHFTextField
                name={`empExp[${index}].compNm`}
                label={
                  <div>
                    Employer Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
              />

              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empExp[${index}].designation`}
                  placeholder="Select designation"
                  label={
                    <div>
                      Designation<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={desgData}
                  getOptionLabel={(option) => option.designationName}
                  //   value={selectedGender}
                  //   onChange={(event, newValue) => {
                  //     setSelectedGender(newValue);
                  //   }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.designationName}>
                      {option.designationName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empExp[${index}].country`}
                  placeholder="Employer Country"
                  label="Employer Country"
                  options={cntryData}
                  onChange={(event, value) => {
                    setValue(`empExp[${index}].country`, value, { shouldValidate: true });
                    setValue(`empExp[${index}].ctccurncy`, value)
                  }}
                  getOptionLabel={(option) => option.countryName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.countriesId}>
                      {option.countryName}
                    </li>
                  )}
                />
              </Stack>
              <RHFTextField
                name={`empExp[${index}].ctc`}
                label="CTC"
                placeholder="0"
                type="number"
                onWheel={(e) => e.target.blur()}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        <RHFAutocomplete
                          name={`empExp[${index}].ctccurncy`}
                          filterOptions={filterCurrency}
                          sx={{
                            '& fieldset': { border: 'none' },
                          }}
                          options={cntryData}
                          getOptionLabel={(option) => option.currencySymbol}
                          onChange={(event, value) => {
                            setValue(`empExp[${index}].ctccurncy`, value, { shouldValidate: true });
                            trigger(`empExp[${index}].ctc`)
                          }}
                          componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                          renderOption={(props, option) => (
                            <li {...props} key={option.shortName}>
                              {option.countryName}({option.currencySymbol})
                            </li>
                          )}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

            </Box>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name={`empExp[${index}].expfromDate`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={
                      <div>
                        From Date<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                      </div>
                    }
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empExp[${index}].exptoDate`);
                      trigger(`empExp[${index}].expfromDate`);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    maxDate={new Date()}
                  />
                )}
              />
              <Controller
                name={`empExp[${index}].exptoDate`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={
                      <div>
                        To Date<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                      </div>
                    }
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empExp[${index}].expfromDate`);
                      trigger(`empExp[${index}].exptoDate`);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
EmpExpNewEditForm.propTypes = {
  desgData: PropTypes.array,
  cntryData: PropTypes.array,
  filterCurrency: PropTypes.func,
  trigger: PropTypes.func,
  switchState: PropTypes.object,
  onEmptyExperience: PropTypes.func
};
